import {
    ChevronDownIcon,
    ChevronUpIcon,
    LinkIcon,
    SparklesIcon,
    TrashIcon,
} from '@heroicons/react/24/outline'
import React, { useEffect, useRef, useState } from 'react'
import apiService from '../api/apiService'
import { useAuth } from '../contexts/AuthContext'
import { useUndo } from '../contexts/UndoContext'
import ImageUpload from './ImageUpload'
import { Button, Card, Input, TextArea } from './ui'

// Placeholder image URL for testing
const DEFAULT_IMAGE =
  'https://images.unsplash.com/photo-1495521821757-a1efb6729352?q=80&w=800&auto=format&fit=crop'

// Common measurement units for cooking
const MEASUREMENT_UNITS = [
  { value: '', label: 'Unit' },
  { value: 'g', label: 'Grams (g)' },
  { value: 'kg', label: 'Kilograms (kg)' },
  { value: 'mL', label: 'Milliliters (mL)' },
  { value: 'L', label: 'Liters (L)' },
  { value: 'tsp', label: 'Teaspoon (tsp)' },
  { value: 'tbsp', label: 'Tablespoon (tbsp)' },
  { value: 'oz', label: 'Ounce (oz)' },
  { value: 'lb', label: 'Pound (lb)' },
  { value: 'cup', label: 'Cup' },
  { value: 'pinch', label: 'Pinch' },
  { value: 'piece', label: 'Piece' },
  { value: 'slice', label: 'Slice' },
  { value: 'whole', label: 'Whole' },
  { value: 'bunch', label: 'Bunch' },
  { value: 'clove', label: 'Clove' },
  { value: 'can', label: 'Can' },
  { value: 'package', label: 'Package' },
]

const IngredientItem = ({
  ingredient,
  index,
  handleIngredientChange,
  handleRemoveIngredient,
  ingredients,
  setIngredients,
  handleDragStart,
  handleDragOver,
  handleDragEnd,
  handleDrop,
  openUnitDropdownIndex,
  setOpenUnitDropdownIndex,
  selectedDropdownIndex,
  setSelectedDropdownIndex,
  dropdownRefs,
}) => {
  return (
    <div
      className="w-full sm:w-[calc(50%-0.5rem)] rounded-md py-1"
      draggable="true"
      onDragStart={(e) => handleDragStart(e, index)}
      onDragOver={(e) => handleDragOver(e)}
      onDragEnd={(e) => handleDragEnd(e)}
      onDrop={(e) => handleDrop(e, index)}
    >
      {/* Desktop Layout (sm and up) */}
      <div className="hidden sm:flex gap-1 items-center">
        <div
          className="flex items-center justify-center cursor-grab relative group mr-2 rounded-full w-7 h-7 bg-blue-100 hover:bg-blue-200 transition-colors flex-shrink-0"
          title="Drag to reorder"
          tabIndex="-1"
        >
          <div className="text-sm font-semibold text-blue-700 pointer-events-none">
            {index + 1}
          </div>
        </div>
        <Input
          value={ingredient.item}
          onChange={(e) =>
            handleIngredientChange(index, 'item', e.target.value)
          }
          placeholder="Ingredient"
          className="flex-[3] text-sm lg:text-md min-w-[120px]"
          bottomGap={false}
          required
        />
        <Input
          value={ingredient.amount}
          onChange={(e) =>
            handleIngredientChange(index, 'amount', e.target.value)
          }
          placeholder="#"
          className="flex-1 w-[60px] min-w-[60px] text-sm lg:text-md"
          type="number"
          bottomGap={false}
        />
        <div
          className="flex-1 min-w-[80px]"
          ref={(el) => (dropdownRefs.current[index] = el)}
        >
          <input
            type="text"
            value={
              ingredient.unit
                ? MEASUREMENT_UNITS.find((u) => u.value === ingredient.unit)
                    ?.label || ingredient.unit
                : ''
            }
            onChange={(e) => {
              const inputValue = e.target.value
              if (!inputValue.trim()) {
                handleIngredientChange(index, 'unit', '')
                return
              }
              handleIngredientChange(index, 'unit', inputValue)
              setOpenUnitDropdownIndex(index)
              setSelectedDropdownIndex(0)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && ingredient.unit) {
                const currentLabel = MEASUREMENT_UNITS.find(
                  (u) => u.value === ingredient.unit
                )?.label
                if (currentLabel) {
                  e.preventDefault()
                  handleIngredientChange(index, 'unit', '')
                }
              }
              const filteredUnits = MEASUREMENT_UNITS.filter(
                (unit) =>
                  unit.label
                    .toLowerCase()
                    .includes((ingredient.unit || '').toLowerCase()) &&
                  unit.value !== ''
              )
              if (e.key === 'Enter' && openUnitDropdownIndex === index) {
                e.preventDefault()
                if (filteredUnits.length > 0) {
                  handleIngredientChange(
                    index,
                    'unit',
                    filteredUnits[selectedDropdownIndex].value
                  )
                  setOpenUnitDropdownIndex(null)
                  setSelectedDropdownIndex(0)
                  const form = e.target.closest('form')
                  if (form) {
                    const inputs = Array.from(
                      form.querySelectorAll(
                        'input, textarea, button:not([type="text-danger"]'
                      )
                    )
                    const currentIndex = inputs.indexOf(e.target)
                    const nextInput = inputs[currentIndex + 1]
                    if (nextInput) {
                      nextInput.focus()
                    }
                  }
                }
              }
              if (e.key === 'Tab') {
                setOpenUnitDropdownIndex(null)
                setSelectedDropdownIndex(0)
              }
            }}
            onFocus={() => {
              setOpenUnitDropdownIndex(index)
              setSelectedDropdownIndex(0)
            }}
            onBlur={(e) => {
              setTimeout(() => {
                setOpenUnitDropdownIndex(null)
                setSelectedDropdownIndex(0)
              }, 200)
            }}
            placeholder="Unit"
            className="text-sm lg:text-md w-full h-full pl-2 lg:px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 h-[38px]"
          />
          {openUnitDropdownIndex === index && (
            <div className="absolute z-10 mt-1 w-auto min-w-[120px] whitespace-nowrap bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
              {MEASUREMENT_UNITS.filter(
                (unit) =>
                  unit.label
                    .toLowerCase()
                    .includes((ingredient.unit || '').toLowerCase()) &&
                  unit.value !== ''
              ).map((unit, dropdownIndex) => (
                <div
                  key={unit.value}
                  className={`text-sm px-3 py-1.5 cursor-pointer ${
                    dropdownIndex === selectedDropdownIndex
                      ? 'bg-green-100 hover:bg-green-200'
                      : 'hover:bg-gray-100'
                  }`}
                  onClick={() => {
                    handleIngredientChange(index, 'unit', unit.value)
                    setOpenUnitDropdownIndex(null)
                    setSelectedDropdownIndex(0)
                  }}
                >
                  {unit.label}
                </div>
              ))}
            </div>
          )}
        </div>
        {ingredients.length > 1 && (
          <Button
            type="button"
            variant="text-danger"
            size="sm"
            className="text-red-600 p-1 flex-shrink-0"
            onClick={() => handleRemoveIngredient(index)}
            tabIndex="-1"
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Mobile Layout (below sm) */}
      <div className="flex sm:hidden gap-2 items-center">
        {/* Column 1: Row Number */}
        <div
          className="flex items-center justify-center cursor-grab relative group rounded-full w-8 h-8 bg-blue-100 hover:bg-blue-200 transition-colors flex-shrink-0"
          title="Drag to reorder"
          tabIndex="-1"
        >
          <div className="text-sm font-semibold text-blue-700 pointer-events-none">
            {index + 1}
          </div>
        </div>

        {/* Column 2: Ingredient Name (top) and Quantity/Unit (bottom) */}
        <div className="flex-1 space-y-1">
          {/* Ingredient Name */}
          <Input
            value={ingredient.item}
            onChange={(e) =>
              handleIngredientChange(index, 'item', e.target.value)
            }
            placeholder="Ingredient"
            className="w-full text-sm"
            bottomGap={false}
            required
          />

          {/* Quantity and Unit Row */}
          <div className="flex gap-1">
            <div className="flex-1">
              <Input
                value={ingredient.amount}
                onChange={(e) =>
                  handleIngredientChange(index, 'amount', e.target.value)
                }
                placeholder="#"
                className="w-full text-sm"
                type="number"
                bottomGap={false}
              />
            </div>
            <div
              className="flex-1"
              ref={(el) => (dropdownRefs.current[index] = el)}
            >
              <input
                type="text"
                value={
                  ingredient.unit
                    ? MEASUREMENT_UNITS.find((u) => u.value === ingredient.unit)
                        ?.label || ingredient.unit
                    : ''
                }
                onChange={(e) => {
                  const inputValue = e.target.value
                  if (!inputValue.trim()) {
                    handleIngredientChange(index, 'unit', '')
                    return
                  }
                  handleIngredientChange(index, 'unit', inputValue)
                  setOpenUnitDropdownIndex(index)
                  setSelectedDropdownIndex(0)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace' && ingredient.unit) {
                    const currentLabel = MEASUREMENT_UNITS.find(
                      (u) => u.value === ingredient.unit
                    )?.label
                    if (currentLabel) {
                      e.preventDefault()
                      handleIngredientChange(index, 'unit', '')
                    }
                  }
                  const filteredUnits = MEASUREMENT_UNITS.filter(
                    (unit) =>
                      unit.label
                        .toLowerCase()
                        .includes((ingredient.unit || '').toLowerCase()) &&
                      unit.value !== ''
                  )
                  if (e.key === 'Enter' && openUnitDropdownIndex === index) {
                    e.preventDefault()
                    if (filteredUnits.length > 0) {
                      handleIngredientChange(
                        index,
                        'unit',
                        filteredUnits[selectedDropdownIndex].value
                      )
                      setOpenUnitDropdownIndex(null)
                      setSelectedDropdownIndex(0)
                      const form = e.target.closest('form')
                      if (form) {
                        const inputs = Array.from(
                          form.querySelectorAll(
                            'input, textarea, button:not([type="text-danger"]'
                          )
                        )
                        const currentIndex = inputs.indexOf(e.target)
                        const nextInput = inputs[currentIndex + 1]
                        if (nextInput) {
                          nextInput.focus()
                        }
                      }
                    }
                  }
                  if (e.key === 'Tab') {
                    setOpenUnitDropdownIndex(null)
                    setSelectedDropdownIndex(0)
                  }
                }}
                onFocus={() => {
                  setOpenUnitDropdownIndex(index)
                  setSelectedDropdownIndex(0)
                }}
                onBlur={(e) => {
                  setTimeout(() => {
                    setOpenUnitDropdownIndex(null)
                    setSelectedDropdownIndex(0)
                  }, 200)
                }}
                placeholder="Unit"
                className="text-sm w-full px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 h-[38px]"
              />
              {openUnitDropdownIndex === index && (
                <div className="absolute z-10 mt-1 w-auto min-w-[120px] whitespace-nowrap bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {MEASUREMENT_UNITS.filter(
                    (unit) =>
                      unit.label
                        .toLowerCase()
                        .includes((ingredient.unit || '').toLowerCase()) &&
                      unit.value !== ''
                  ).map((unit, dropdownIndex) => (
                    <div
                      key={unit.value}
                      className={`text-sm px-3 py-1.5 cursor-pointer ${
                        dropdownIndex === selectedDropdownIndex
                          ? 'bg-green-100 hover:bg-green-200'
                          : 'hover:bg-gray-100'
                      }`}
                      onClick={() => {
                        handleIngredientChange(index, 'unit', unit.value)
                        setOpenUnitDropdownIndex(null)
                        setSelectedDropdownIndex(0)
                      }}
                    >
                      {unit.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Column 3: Delete Button */}
        {ingredients.length > 1 && (
          <Button
            type="button"
            variant="text-danger"
            size="xs"
            className="text-red-600 p-0 flex-shrink-0"
            onClick={() => handleRemoveIngredient(index)}
            tabIndex="-1"
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  )
}

const InstructionItem = ({
  instruction,
  index,
  handleInstructionChange,
  handleRemoveInstruction,
  instructions,
  setInstructions,
  handleDragStart,
  handleDragOver,
  handleDragEnd,
  handleDrop,
}) => {
  return (
    <div
      className="flex gap-1 items-center w-full rounded-md py-1"
      draggable="true"
      onDragStart={(e) => handleDragStart(e, index)}
      onDragOver={(e) => handleDragOver(e)}
      onDragEnd={(e) => handleDragEnd(e)}
      onDrop={(e) => handleDrop(e, index)}
    >
      <div
        className="flex items-center justify-center self-center cursor-grab relative group sm:mr-2 rounded-full w-7 h-7 bg-green-100 hover:bg-green-200 transition-colors flex-shrink-0"
        title="Drag to reorder"
        tabIndex="-1"
      >
        <div className="text-sm font-semibold text-green-700 pointer-events-none">
          {index + 1}
        </div>
      </div>
      <TextArea
        value={instruction}
        onChange={(e) => handleInstructionChange(index, e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            const newInstructions = [...instructions]
            newInstructions.splice(index + 1, 0, '')
            setInstructions(newInstructions)
            setTimeout(() => {
              const form = e.target.closest('form')
              if (form) {
                const textareas = form.querySelectorAll('textarea')
                const nextTextarea = textareas[index + 1]
                if (nextTextarea) nextTextarea.focus()
              }
            }, 0)
          }
        }}
        placeholder={`Step ${index + 1}`}
        rows={1}
        className="text-sm lg:text-md"
      />
      {instructions.length > 1 && (
        <Button
          type="button"
          variant="text-danger"
          size="xs"
          className="text-red-600"
          onClick={() => handleRemoveInstruction(index)}
          tabIndex="-1"
        >
          <TrashIcon className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}

const RecipeForm = ({ recipe, copyingRecipe, onRecipeSaved, onCancel }) => {
  const { user, dbUser } = useAuth()
  const [name, setName] = useState('')
  const [ingredients, setIngredients] = useState([
    { item: '', amount: '', unit: '' },
    { item: '', amount: '', unit: '' },
  ])
  const [instructions, setInstructions] = useState([''])
  const [recipeUrl, setRecipeUrl] = useState('')
  const [recipeImages, setRecipeImages] = useState([])
  const [cookingTimeMinutes, setCookingTimeMinutes] = useState('')
  const [loading, setLoading] = useState(false)
  const [originalRecipe, setOriginalRecipe] = useState(null)
  const [validationError, setValidationError] = useState('')
  const [checkingDuplicate, setCheckingDuplicate] = useState(false)
  const { addToUndoStack } = useUndo()
  const [recipeUrlInputFocused, setRecipeUrlInputFocused] = useState(false)
  const [cookingTimeInputFocused, setCookingTimeInputFocused] = useState(false)
  const [showImageUrlInput, setShowImageUrlInput] = useState(true)
  const [imageUrlInput, setImageUrlInput] = useState('')
  const [userId, setUserId] = useState(null)

  // State for custom unit dropdowns
  const [openUnitDropdownIndex, setOpenUnitDropdownIndex] = useState(null)
  const [selectedDropdownIndex, setSelectedDropdownIndex] = useState(0)

  const dropdownRefs = useRef([])

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutside = !dropdownRefs.current.some(
        (ref) => ref && ref.contains(event.target)
      )
      if (clickedOutside) {
        setOpenUnitDropdownIndex(null)
        setSelectedDropdownIndex(0)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Fetch user ID if not available from context
  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const userData = await apiService.getUserIdByEmail(user?.email)
        if (userData) setUserId(userData.id)
      } catch (error) {
        console.error('Error fetching user ID:', error)
        setValidationError('Failed to fetch user data. Please try again.')
      }
    }

    if (!dbUser?.id && user?.email) {
      fetchUserId()
    } else if (dbUser?.id) {
      setUserId(dbUser.id)
    }
  }, [dbUser, user])

  // Update form fields when recipe or copyingRecipe prop changes
  useEffect(() => {
    if (copyingRecipe) {
      // Store the original recipe for comparison when copying
      setOriginalRecipe(copyingRecipe)

      setName(copyingRecipe.name || '')
      setCookingTimeMinutes(copyingRecipe.cooking_time_minutes || '')
      try {
        // Handle null ingredients
        if (copyingRecipe.ingredients === null) {
          setIngredients([
            { item: '', amount: '', unit: '' },
            { item: '', amount: '', unit: '' },
          ])
        } else {
          setIngredients(
            JSON.parse(copyingRecipe.ingredients) || [
              { item: '', amount: '', unit: '' },
              { item: '', amount: '', unit: '' },
            ]
          )
        }

        // Handle null instructions
        if (copyingRecipe.instructions === null) {
          setInstructions([''])
        } else if (Array.isArray(copyingRecipe.instructions)) {
          // If instructions is already an array, use it directly
          setInstructions(
            copyingRecipe.instructions.length > 0
              ? copyingRecipe.instructions
              : ['']
          )
        } else {
          // Otherwise try to parse it as JSON
          setInstructions(JSON.parse(copyingRecipe.instructions) || [''])
        }
      } catch (error) {
        setIngredients([
          { item: '', amount: '', unit: '' },
          { item: '', amount: '', unit: '' },
        ])
        setInstructions([''])
      }
      setRecipeUrl(copyingRecipe.recipe_url || '')

      // Initialize images from recipe_images if available, otherwise from image_url
      if (
        copyingRecipe.recipe_images &&
        copyingRecipe.recipe_images.length > 0
      ) {
        // Sort by display_order using consistent logic
        const sortedImages = [...copyingRecipe.recipe_images].sort((a, b) => {
          // Primary sort by display_order (ascending - 1-based, so 1, 2, 3...)
          if (a.display_order !== b.display_order) {
            return (a.display_order || 999) - (b.display_order || 999)
          }
          // Secondary sort by created_at (newest first) if display_order is the same
          return new Date(b.created_at || 0) - new Date(a.created_at || 0)
        })
        setRecipeImages(sortedImages.map((img) => img.image_url))
      } else if (copyingRecipe.image_url) {
        setRecipeImages([copyingRecipe.image_url])
      } else {
        setRecipeImages([])
      }
    } else if (recipe) {
      setOriginalRecipe(recipe)

      setName(recipe.name || '')
      setCookingTimeMinutes(recipe.cooking_time_minutes || '')
      try {
        // Handle null ingredients
        if (recipe.ingredients === null) {
          setIngredients([
            { item: '', amount: '', unit: '' },
            { item: '', amount: '', unit: '' },
          ])
        } else {
          setIngredients(
            JSON.parse(recipe.ingredients) || [
              { item: '', amount: '', unit: '' },
              { item: '', amount: '', unit: '' },
            ]
          )
        }

        // Handle null instructions
        if (recipe.instructions === null) {
          setInstructions([''])
        } else if (Array.isArray(recipe.instructions)) {
          // If instructions is already an array, use it directly
          setInstructions(
            recipe.instructions.length > 0 ? recipe.instructions : ['']
          )
        } else {
          // Otherwise try to parse it as JSON
          setInstructions(JSON.parse(recipe.instructions) || [''])
        }
      } catch (error) {
        setIngredients([
          { item: '', amount: '', unit: '' },
          { item: '', amount: '', unit: '' },
        ])
        setInstructions([''])
      }
      setRecipeUrl(recipe.recipe_url || '')

      // Initialize images from recipe_images if available, otherwise from image_url
      if (recipe.recipe_images && recipe.recipe_images.length > 0) {
        // Sort by display_order using consistent logic
        const sortedImages = [...recipe.recipe_images].sort((a, b) => {
          // Primary sort by display_order (ascending - 1-based, so 1, 2, 3...)
          if (a.display_order !== b.display_order) {
            return (a.display_order || 999) - (b.display_order || 999)
          }
          // Secondary sort by created_at (newest first) if display_order is the same
          return new Date(b.created_at || 0) - new Date(a.created_at || 0)
        })
        setRecipeImages(sortedImages.map((img) => img.image_url))
      } else if (recipe.image_url) {
        setRecipeImages([recipe.image_url])
      } else {
        setRecipeImages([])
      }
    } else {
      setOriginalRecipe(null)
      setName('')
      setIngredients([
        { item: '', amount: '', unit: '' },
        { item: '', amount: '', unit: '' },
      ])
      setInstructions([''])
      setRecipeUrl('')
      setRecipeImages([])
      setCookingTimeMinutes('')
    }

    // Clear any validation errors when the form changes
    setValidationError('')
  }, [recipe, copyingRecipe])

  // Check if at least one field has changed when copying a recipe
  const hasChangedField = () => {
    if (!originalRecipe || !copyingRecipe) return true

    // Check if name has changed
    if (name !== originalRecipe.name) return true

    // Check if recipe URL has changed
    if (recipeUrl !== (originalRecipe.recipe_url || '')) return true

    // Check if images have changed
    const originalImages = originalRecipe.recipe_images
      ? originalRecipe.recipe_images.map((img) => img.image_url).sort()
      : originalRecipe.image_url
        ? [originalRecipe.image_url]
        : []

    if (JSON.stringify(recipeImages.sort()) !== JSON.stringify(originalImages))
      return true

    // Check if cooking time has changed
    if (cookingTimeMinutes !== (originalRecipe.cooking_time_minutes || ''))
      return true

    // Check if ingredients have changed
    try {
      const originalIngredients = JSON.parse(originalRecipe.ingredients || '[]')
      if (JSON.stringify(ingredients) !== JSON.stringify(originalIngredients))
        return true
    } catch (error) {
      return true
    }

    // Check if instructions have changed
    try {
      let originalInstructions
      if (Array.isArray(originalRecipe.instructions)) {
        originalInstructions = originalRecipe.instructions
      } else {
        originalInstructions = JSON.parse(originalRecipe.instructions || '[]')
      }
      if (JSON.stringify(instructions) !== JSON.stringify(originalInstructions))
        return true
    } catch (error) {
      return true
    }

    return false
  }

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...ingredients]
    // Convert amount to number if it's the amount field
    if (field === 'amount') {
      value = value === '' ? '' : Number(value)
    }
    newIngredients[index] = { ...newIngredients[index], [field]: value }
    setIngredients(newIngredients)

    // Clear validation error when user makes changes
    setValidationError('')
  }

  const handleAddIngredient = () => {
    setIngredients([...ingredients, { item: '', amount: '', unit: '' }])
  }

  const handleRemoveIngredient = (index) => {
    if (ingredients.length > 1) {
      setIngredients(ingredients.filter((_, i) => i !== index))
    }
  }

  const handleInstructionChange = (index, value) => {
    const newInstructions = [...instructions]
    newInstructions[index] = value
    setInstructions(newInstructions)
  }

  const handleAddInstruction = () => {
    setInstructions([...instructions, ''])
  }

  const handleRemoveInstruction = (index) => {
    if (instructions.length > 1) {
      setInstructions(instructions.filter((_, i) => i !== index))
    }
  }

  // Check for duplicate recipe names
  const checkDuplicateRecipe = async (recipeName) => {
    try {
      setCheckingDuplicate(true)

      const result = await apiService.checkDuplicateRecipe(recipeName)
      
      // If editing an existing recipe, we may need additional logic
      // For now, the API handles this correctly
      return result.isDuplicate
    } catch (error) {
      return false
    } finally {
      setCheckingDuplicate(false)
    }
  }

  const handleAddImage = (url) => {
    if (!url || recipeImages.includes(url)) return
    setRecipeImages([...recipeImages, url])
    setImageUrlInput('')
  }

  const handleRemoveImage = (index) => {
    const newImages = [...recipeImages]
    newImages.splice(index, 1)
    setRecipeImages(newImages)
  }

  const handleMoveImage = (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === recipeImages.length - 1)
    ) {
      return
    }

    const newImages = [...recipeImages]
    const targetIndex = direction === 'up' ? index - 1 : index + 1

    // Swap the images
    ;[newImages[index], newImages[targetIndex]] = [
      newImages[targetIndex],
      newImages[index],
    ]

    setRecipeImages(newImages)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate required fields
    if (!name.trim()) {
      alert('Please enter a recipe name')
      return
    }

    // Check if any ingredients are empty
    const hasEmptyIngredient = ingredients.some((ing) => !ing.item.trim())
    if (hasEmptyIngredient) {
      alert('Please fill in all ingredient names or remove empty ingredients')
      return
    }

    // When copying a recipe, ensure at least one field has changed
    if (copyingRecipe && !hasChangedField()) {
      setValidationError(
        'Please change at least one field when copying a recipe.'
      )
      return
    }

    // Check for duplicate recipe names
    const isDuplicate = await checkDuplicateRecipe(name.trim())
    if (isDuplicate) {
      setValidationError(
        'A recipe with this name already exists. Please use a different name.'
      )
      return
    }

    setLoading(true)

    try {
      if (!userId) {
        throw new Error('Please sign in to create or edit recipes')
      }

      // Format ingredients with the new structure
      const formattedIngredients = ingredients
        .filter((ing) => ing.item.trim() !== '')
        .map(({ item, amount, unit }) => ({
          item: item.trim(),
          amount: amount || '',
          unit: unit || '',
        }))

      // Filter out empty instructions
      const filteredInstructions = instructions.filter((inst) => inst.trim())

      // Use default image if none provided
      const finalImages =
        recipeImages.length > 0 ? recipeImages : [DEFAULT_IMAGE]

      // Prepare recipe data
      const recipeData = {
        name: name.trim(),
        ingredients: JSON.stringify(formattedIngredients),
        instructions:
          filteredInstructions.length > 0 ? filteredInstructions : [],
        recipe_url: recipeUrl || null,
        images: finalImages,
        cooking_time_minutes: cookingTimeMinutes
          ? parseInt(cookingTimeMinutes)
          : null,
        user_id: userId,
      }

      if (recipe && !copyingRecipe) {
        // Store the complete previous state before updating
        const previousData = {
          id: recipe.id,
          name: recipe.name,
          ingredients: recipe.ingredients,
          instructions: recipe.instructions,
          recipe_url: recipe.recipe_url,
          image_url: recipe.image_url,
          recipe_images: recipe.recipe_images,
          cooking_time_minutes: recipe.cooking_time_minutes,
          created_at: recipe.created_at,
          user_id: recipe.user_id,
        }

        // Update existing recipe using API service
        const updatedRecipe = await apiService.updateRecipe(recipe.id, recipeData)

        // Add to undo stack with complete previous data
        addToUndoStack({
          type: 'update',
          data: updatedRecipe,
          previousData: previousData,
        })
      } else {
        // Insert new recipe using API service
        const newRecipe = await apiService.createRecipe(recipeData)

        // Add to undo stack
        addToUndoStack({
          type: 'create',
          data: newRecipe,
        })
      }

      // Call onRecipeSaved to close the form and refresh the recipe list
      onRecipeSaved()
    } catch (error) {
      setValidationError(
        error.message || 'Failed to save recipe. Please try again.'
      )
      console.error('Error saving recipe:', error)
    } finally {
      setLoading(false)
    }
  }

  const renderImageSection = () => (
    <div className="space-y-4">
      {/* Simple instructions */}
      {recipeImages.length > 0 ? (
        <p className="text-sm text-gray-500 mt-2">
          ℹ️ The first image will be the main image.
        </p>
      ) : (
        <p className="text-sm text-gray-500 mt-2">
          ℹ️ Add at least one photo of your dish.
        </p>
      )}

      {/* Image thumbnail grid - show if there are images */}
      {recipeImages.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {recipeImages.map((imageUrl, index) => (
            <div key={index} className="relative group">
              <div className="w-24 h-24 border rounded overflow-hidden">
                <img
                  src={imageUrl}
                  alt={`Recipe image ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute top-0 right-0 flex">
                <button
                  type="button"
                  className="bg-white rounded-full p-1 shadow-md hover:bg-red-50"
                  onClick={() => handleRemoveImage(index)}
                >
                  <TrashIcon className="h-4 w-4 text-red-600" />
                </button>
              </div>
              {index === 0 && (
                <div className="absolute bottom-0 left-0 right-0 bg-green-500 bg-opacity-70 text-white text-xs py-1 px-2 text-center">
                  Main Image
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity flex justify-between px-1">
                {index > 0 && (
                  <button
                    type="button"
                    className="bg-white rounded-full p-1 shadow-md"
                    onClick={() => handleMoveImage(index, 'up')}
                  >
                    <span className="text-xs">↑</span>
                  </button>
                )}
                {index < recipeImages.length - 1 && (
                  <button
                    type="button"
                    className="bg-white rounded-full p-1 shadow-md ml-auto"
                    onClick={() => handleMoveImage(index, 'down')}
                  >
                    <span className="text-xs">↓</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
        {/* Image upload options */}
        <div className="flex-shrink-0">
          <ImageUpload
            onImageUrlChange={(url) => {
              if (url) handleAddImage(url)
            }}
          />
        </div>

        {/* URL input */}
        {showImageUrlInput && (
          <div className="relative flex-1 min-w-0">
            <div className="flex">
              <input
                type="url"
                value={imageUrlInput}
                onChange={(e) => setImageUrlInput(e.target.value)}
                placeholder="Or paste an image URL"
                className="w-full px-4 py-2 border border-gray-300 rounded-l focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 mb-0 text-sm lg:text-md"
              />
              <Button
                type="button"
                onClick={() => handleAddImage(imageUrlInput)}
                className="rounded-l-none whitespace-nowrap"
                disabled={!imageUrlInput}
              >
                Add
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )

  const [showUrlDropdown, setShowUrlDropdown] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [jsonError, setJsonError] = useState('')
  const [missedFields, setMissedFields] = useState(new Set())
  const urlDropdownRef = useRef(null)
  // Add state for text import mode
  const [importMode, setImportMode] = useState('url') // 'url' or 'text'
  const [recipeText, setRecipeText] = useState('')

  // Add click outside handler for URL dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        urlDropdownRef.current &&
        !urlDropdownRef.current.contains(event.target)
      ) {
        setShowUrlDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const processRecipeUrl = async (url) => {
    setIsProcessing(true)
    setJsonError('')

    try {
      const response = await fetch('/api/process-recipe-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url }),
      })

      // Handle HTTP error responses
      if (!response.ok) {
        let errorMessage = 'Failed to process recipe URL'
        try {
          const errorData = await response.json()
          errorMessage = errorData.error || errorMessage
        } catch (e) {
          // If response.json() fails, use the default error message
        }
        throw new Error(errorMessage)
      }

      // Parse the response text
      const responseText = await response.text()

      // Try to parse as JSON
      let data
      try {
        data = JSON.parse(responseText)
      } catch (e) {
        console.error('JSON parse error:', e)
        console.log('Raw response:', responseText)
        throw new Error(
          'Error parsing recipe data. Please try a different URL.'
        )
      }

      // Ensure we have valid data
      if (!data || typeof data !== 'object') {
        throw new Error('Received invalid data format from server.')
      }

      // Map the JSON structure to our form fields
      if (data.recipe_name || data.name) {
        setName(data.recipe_name || data.name)
      }

      if (data.ingredients && Array.isArray(data.ingredients)) {
        const mappedIngredients = data.ingredients.map((ing) => {
          // Handle potential fraction strings in amount (like "1/2")
          let parsedAmount = ''
          if (ing.amount) {
            if (typeof ing.amount === 'string' && ing.amount.includes('/')) {
              // Parse fraction string (e.g., "1/2")
              const fractionParts = ing.amount.split('/')
              if (fractionParts.length === 2) {
                try {
                  parsedAmount =
                    parseFloat(fractionParts[0]) / parseFloat(fractionParts[1])
                } catch (e) {
                  parsedAmount = ing.amount // Keep as string if parsing fails
                }
              } else {
                parsedAmount = ing.amount
              }
            } else {
              // Use the amount as is
              parsedAmount = ing.amount
            }
          }

          return {
            item: ing.name || ing.item || '',
            amount: parsedAmount,
            unit: mapUnitFromJson(ing.unit || ''),
          }
        })
        setIngredients(mappedIngredients)
      }

      if (data.instructions && Array.isArray(data.instructions)) {
        setInstructions(data.instructions)
      }

      // Use the original URL for the recipe URL field
      setRecipeUrl(url)

      if (data.minutes || data.cooking_time_minutes) {
        setCookingTimeMinutes(
          (data.minutes || data.cooking_time_minutes).toString()
        )
      }

      // Close the dropdown after successful import
      setShowUrlDropdown(false)
    } catch (error) {
      console.error('Error processing recipe URL:', error)
      setJsonError(
        error.message || 'Failed to process recipe URL. Please try again.'
      )
    } finally {
      setIsProcessing(false)
    }
  }

  const processRecipeText = async (text) => {
    setIsProcessing(true)
    setJsonError('')

    try {
      const response = await fetch('/api/process-recipe-text', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text }),
      })

      // Handle HTTP error responses
      if (!response.ok) {
        let errorMessage = 'Failed to process recipe text'
        try {
          const errorData = await response.json()
          errorMessage = errorData.error || errorMessage
        } catch (e) {
          // If response.json() fails, use the default error message
        }
        throw new Error(errorMessage)
      }

      // Parse the response text
      const responseText = await response.text()

      // Try to parse as JSON
      let data
      try {
        data = JSON.parse(responseText)
      } catch (e) {
        console.error('JSON parse error:', e)
        console.log('Raw response:', responseText)
        throw new Error('Error parsing recipe data. Please try different text.')
      }

      // Ensure we have valid data
      if (!data || typeof data !== 'object') {
        throw new Error('Received invalid data format from server.')
      }

      // Map the JSON structure to our form fields (same as URL processing)
      if (data.recipe_name || data.name) {
        setName(data.recipe_name || data.name)
      }

      if (data.ingredients && Array.isArray(data.ingredients)) {
        const mappedIngredients = data.ingredients.map((ing) => {
          // Handle potential fraction strings in amount (like "1/2")
          let parsedAmount = ''
          if (ing.amount) {
            if (typeof ing.amount === 'string' && ing.amount.includes('/')) {
              // Parse fraction string (e.g., "1/2")
              const fractionParts = ing.amount.split('/')
              if (fractionParts.length === 2) {
                try {
                  parsedAmount =
                    parseFloat(fractionParts[0]) / parseFloat(fractionParts[1])
                } catch (e) {
                  parsedAmount = ing.amount // Keep as string if parsing fails
                }
              } else {
                parsedAmount = ing.amount
              }
            } else {
              // Use the amount as is
              parsedAmount = ing.amount
            }
          }

          return {
            item: ing.name || ing.item || '',
            amount: parsedAmount,
            unit: mapUnitFromJson(ing.unit || ''),
          }
        })
        setIngredients(mappedIngredients)
      }

      if (data.instructions && Array.isArray(data.instructions)) {
        setInstructions(data.instructions)
      }

      if (data.minutes || data.cooking_time_minutes) {
        setCookingTimeMinutes(
          (data.minutes || data.cooking_time_minutes).toString()
        )
      }

      // Close the dropdown after successful import
      setShowUrlDropdown(false)
    } catch (error) {
      console.error('Error processing recipe text:', error)
      setJsonError(
        error.message || 'Failed to process recipe text. Please try again.'
      )
    } finally {
      setIsProcessing(false)
    }
  }

  // Helper function to map common unit variations to our standard units
  const mapUnitFromJson = (unit) => {
    if (!unit) return ''

    const unitMap = {
      grams: 'g',
      gram: 'g',
      g: 'g',
      kilograms: 'kg',
      kilogram: 'kg',
      kg: 'kg',
      milliliters: 'mL',
      milliliter: 'mL',
      ml: 'mL',
      liters: 'L',
      liter: 'L',
      l: 'L',
      teaspoons: 'tsp',
      teaspoon: 'tsp',
      tsp: 'tsp',
      tablespoons: 'tbsp',
      tablespoon: 'tbsp',
      tbsp: 'tbsp',
      ounces: 'oz',
      ounce: 'oz',
      oz: 'oz',
      pounds: 'lb',
      pound: 'lb',
      lb: 'lb',
      cups: 'cup',
      cup: 'cup',
      pieces: 'piece',
      piece: 'piece',
      unit: 'piece',
      units: 'piece',
      slices: 'slice',
      slice: 'slice',
      whole: 'whole',
      'to taste': '',
      bunch: 'bunch',
      clove: 'clove',
      can: 'can',
      package: 'package',
    }

    const normalizedUnit = unit.toLowerCase().trim()
    return unitMap[normalizedUnit] || unit
  }

  // Drag handlers for ingredients
  const handleIngredientDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index.toString())
    e.dataTransfer.setData('type', 'ingredient')
    e.currentTarget.classList.add('opacity-50')
  }

  const handleIngredientDragOver = (e) => {
    e.preventDefault()
    e.currentTarget.classList.add('bg-gray-50')
  }

  const handleIngredientDragEnd = (e) => {
    // Remove opacity from all ingredient items
    document.querySelectorAll('.opacity-50').forEach((el) => {
      el.classList.remove('opacity-50')
    })
    // Remove any remaining hover effects
    document.querySelectorAll('.bg-gray-50').forEach((el) => {
      el.classList.remove('bg-gray-50')
    })
  }

  const handleIngredientDrop = (e, dropIndex) => {
    e.preventDefault()
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'))
    const type = e.dataTransfer.getData('type')

    // Only handle ingredient drops
    if (type !== 'ingredient') return

    // Remove visual effects
    document.querySelectorAll('.opacity-50, .bg-gray-50').forEach((el) => {
      el.classList.remove('opacity-50', 'bg-gray-50')
    })

    if (dragIndex === dropIndex) return

    setIngredients((prev) => {
      const newIngredients = [...prev]
      const [draggedItem] = newIngredients.splice(dragIndex, 1)
      newIngredients.splice(dropIndex, 0, draggedItem)
      return newIngredients
    })
  }

  // Drag handlers for instructions
  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index.toString())
    e.dataTransfer.setData('type', 'instruction')
    e.currentTarget.classList.add('opacity-50')
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.currentTarget.classList.add('bg-gray-50')
  }

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('bg-gray-50')
  }

  const handleDragEnd = (e) => {
    // Remove opacity from all instruction items
    document.querySelectorAll('.opacity-50').forEach((el) => {
      el.classList.remove('opacity-50')
    })
    // Remove any remaining hover effects
    document.querySelectorAll('.bg-gray-50').forEach((el) => {
      el.classList.remove('bg-gray-50')
    })
  }

  const handleDrop = (e, dropIndex) => {
    e.preventDefault()
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'))
    const type = e.dataTransfer.getData('type')

    // Only handle instruction drops
    if (type !== 'instruction') return

    // Remove visual effects
    document.querySelectorAll('.opacity-50, .bg-gray-50').forEach((el) => {
      el.classList.remove('opacity-50', 'bg-gray-50')
    })

    if (dragIndex === dropIndex) return

    setInstructions((prev) => {
      const newInstructions = [...prev]
      const [draggedItem] = newInstructions.splice(dragIndex, 1)
      newInstructions.splice(dropIndex, 0, draggedItem)
      return newInstructions
    })
  }

  return (
    <>
      <Card className="mb-6">
        <form onSubmit={handleSubmit}>
          <Card.Body className="space-y-2 sm:space-y-3">
            {validationError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {validationError}
              </div>
            )}

            {/* URL Import Dropdown */}
            <div className="w-full mb-2" ref={urlDropdownRef}>
              <div
                className="w-full bg-gradient-to-r from-green-50 via-emerald-100 to-green-50 rounded-md p-3 cursor-pointer group relative overflow-hidden animate-gradient bg-[length:200%_100%] hover:bg-[length:100%_100%] transition-all duration-300 border border-green-200 shadow-sm flex items-center space-x-2 hover:shadow-md"
                onClick={() => setShowUrlDropdown(!showUrlDropdown)}
                style={{
                  borderBottomLeftRadius: showUrlDropdown ? 0 : undefined,
                  borderBottomRightRadius: showUrlDropdown ? 0 : undefined,
                }}
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center space-x-3">
                    <div className="bg-white p-2 rounded-full shadow-sm relative">
                      <SparklesIcon className="h-5 w-5 text-emerald-600" />
                      <span className="absolute top-0 right-0 flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-green-800 block">
                        Smart Recipe Import
                      </span>
                      <p className="text-xs text-green-600 max-w-sm">
                        Import from URLs or paste recipe text from comments,
                        videos, etc.
                      </p>
                    </div>
                  </div>
                  <div className="text-green-600 flex-shrink-0 mr-1 transition-transform duration-200">
                    {showUrlDropdown ? (
                      <ChevronUpIcon className="h-5 w-5" />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5" />
                    )}
                  </div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-[200%] group-hover:translate-x-[200%] transition-transform duration-[1.5s] ease-in-out"></div>
              </div>

              {showUrlDropdown && (
                <div
                  className="border border-green-200 rounded-b-md bg-white shadow-sm animate-fadeIn"
                  style={{ marginTop: '-1px', borderTop: '0' }}
                >
                  <div className="p-3">
                    {/* Mode Toggle */}
                    <div className="flex mb-3 bg-gray-100 rounded-md p-1">
                      <button
                        type="button"
                        className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-all ${
                          importMode === 'url'
                            ? 'bg-white text-green-700 shadow-sm'
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                        onClick={() => {
                          setImportMode('url')
                          setJsonError('')
                        }}
                      >
                        📄 URL
                      </button>
                      <button
                        type="button"
                        className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-all ${
                          importMode === 'text'
                            ? 'bg-white text-green-700 shadow-sm'
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                        onClick={() => {
                          setImportMode('text')
                          setJsonError('')
                        }}
                      >
                        📝 Text
                      </button>
                    </div>
                    {importMode === 'url' ? (
                      <div className="flex items-stretch">
                        <div className="flex-grow relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <LinkIcon className="h-5 w-5 text-gray-400" />
                          </div>
                          {isProcessing ? (
                            <div className="relative w-full h-[42px]">
                              <div className="absolute inset-0 bg-gradient-to-r from-green-50 to-green-100 flex items-center rounded-l-md overflow-hidden">
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-shimmer" />
                                <div className="flex items-center pl-3">
                                  <svg
                                    className="animate-spin mr-2 h-4 w-4 text-green-600"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  <span className="text-green-700 text-sm">
                                    Generating your recipe...
                                  </span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <input
                              type="url"
                              value={recipeUrl}
                              onChange={(e) => {
                                setRecipeUrl(e.target.value)
                                setJsonError('')
                              }}
                              placeholder="https://cooking.nytimes.com/recipes/..."
                              className="block w-full pl-10 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:border-green-500 focus:ring-0 transition-all duration-200 text-sm lg:text-base h-[42px]"
                              autoFocus
                            />
                          )}
                        </div>
                        {!isProcessing && (
                          <Button
                            type="button"
                            onClick={() => processRecipeUrl(recipeUrl)}
                            disabled={!recipeUrl.trim()}
                            variant="outline"
                            className="rounded-l-none whitespace-nowrap px-4 h-[42px] flex items-center justify-center bg-gradient-to-r from-green-50 via-emerald-100 to-green-50 hover:from-green-100 hover:via-emerald-200 hover:to-green-100 font-medium text-emerald-800 shadow-md hover:shadow-lg transition-all duration-200 border border-green-200"
                            style={{ minHeight: '42px' }}
                          >
                            <span className="text-emerald-800">✨ Import</span>
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {isProcessing ? (
                          <div className="relative w-full h-[120px]">
                            <div className="absolute inset-0 bg-gradient-to-r from-green-50 to-green-100 flex items-center justify-center rounded-md overflow-hidden">
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-shimmer" />
                              <div className="flex items-center">
                                <svg
                                  className="animate-spin mr-2 h-4 w-4 text-green-600"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                                <span className="text-green-700 text-sm">
                                  Parsing your recipe text...
                                </span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <textarea
                              value={recipeText}
                              onChange={(e) => {
                                setRecipeText(e.target.value)
                                setJsonError('')
                              }}
                              placeholder="Paste your recipe text here - ingredients, instructions, or both!"
                              className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-green-500 focus:ring-0 transition-all duration-200 text-sm resize-none"
                              autoFocus
                            />
                            <Button
                              type="button"
                              onClick={() => processRecipeText(recipeText)}
                              disabled={!recipeText.trim()}
                              variant="outline"
                              className="w-full bg-gradient-to-r from-green-50 via-emerald-100 to-green-50 hover:from-green-100 hover:via-emerald-200 hover:to-green-100 font-medium text-emerald-800 shadow-md hover:shadow-lg transition-all duration-200 border border-green-200"
                            >
                              <span className="text-emerald-800">✨ Create Recipe from Text</span>
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                    {jsonError && (
                      <p className="text-red-500 text-sm mt-1">{jsonError}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <Input
              label="Recipe Name"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setValidationError('')
              }}
              placeholder="Enter recipe name"
              required
              className={missedFields.has('name') ? 'border-red-200' : ''}
            />

            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-700 whitespace-nowrap">
                  Ingredients <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2 items-center">
                  {ingredients.length > 1 && (
                    <span className="text-xs text-gray-500 italic">
                      Drag numbers to move
                    </span>
                  )}
                  <Button
                    type="button"
                    variant="text"
                    size="sm"
                    onClick={handleAddIngredient}
                    tabIndex="-1"
                  >
                    + Add
                  </Button>
                </div>
              </div>
              <div
                className={`flex flex-wrap gap-2 ${missedFields.has('ingredients') ? 'border border-red-200 rounded p-2' : ''}`}
              >
                {ingredients.map((ingredient, index) => (
                  <IngredientItem
                    key={index}
                    ingredient={ingredient}
                    index={index}
                    handleIngredientChange={handleIngredientChange}
                    handleRemoveIngredient={handleRemoveIngredient}
                    ingredients={ingredients}
                    setIngredients={setIngredients}
                    handleDragStart={handleIngredientDragStart}
                    handleDragOver={handleIngredientDragOver}
                    handleDragEnd={handleIngredientDragEnd}
                    handleDrop={handleIngredientDrop}
                    openUnitDropdownIndex={openUnitDropdownIndex}
                    setOpenUnitDropdownIndex={setOpenUnitDropdownIndex}
                    selectedDropdownIndex={selectedDropdownIndex}
                    setSelectedDropdownIndex={setSelectedDropdownIndex}
                    dropdownRefs={dropdownRefs}
                  />
                ))}
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-700">
                  Instructions <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2 items-center">
                  {instructions.length > 1 && (
                    <span className="text-xs text-gray-500 italic">
                      Drag numbers to move
                    </span>
                  )}
                  <Button
                    type="button"
                    variant="text"
                    className="whitespace-nowrap"
                    size="sm"
                    onClick={handleAddInstruction}
                    tabIndex="-1"
                  >
                    + Add
                  </Button>
                </div>
              </div>
              <div
                className={`flex flex-col gap-2 ${missedFields.has('instructions') ? 'border border-red-200 rounded p-2' : ''}`}
              >
                <div className="flex flex-col gap-2">
                  {instructions.map((instruction, index) => (
                    <InstructionItem
                      key={index}
                      instruction={instruction}
                      index={index}
                      handleInstructionChange={handleInstructionChange}
                      handleRemoveInstruction={handleRemoveInstruction}
                      instructions={instructions}
                      setInstructions={setInstructions}
                      handleDragStart={handleDragStart}
                      handleDragOver={handleDragOver}
                      handleDragEnd={handleDragEnd}
                      handleDrop={handleDrop}
                    />
                  ))}
                </div>
              </div>
            </div>

            <label className="block text-sm font-medium text-gray-700">
              Metadata
            </label>

            <div>{renderImageSection()}</div>

            <div className="flex gap-4 w-full justify-around flex-col sm:flex-row">
              <div className="relative w-full sm:w-[70%]">
                <input
                  type="url"
                  value={recipeUrl}
                  onChange={(e) => setRecipeUrl(e.target.value)}
                  onFocus={() => {
                    setRecipeUrlInputFocused(true)
                  }}
                  onBlur={() => setRecipeUrlInputFocused(false)}
                  placeholder="Recipe URL"
                  className="text-sm lg:text-md w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 mb-0"
                />
                {(recipeUrl || recipeUrlInputFocused) && (
                  <span className="absolute -top-2 left-2 px-1 text-xs font-medium bg-white text-gray-700">
                    Recipe URL
                  </span>
                )}
              </div>
              <div className="relative w-full sm:w-[30%]">
                <input
                  type="number"
                  value={cookingTimeMinutes}
                  onChange={(e) => setCookingTimeMinutes(e.target.value)}
                  onFocus={() => {
                    setCookingTimeInputFocused(true)
                  }}
                  onBlur={() => setCookingTimeInputFocused(false)}
                  placeholder="Minutes"
                  min="0"
                  className="text-sm lg:text-md w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 mb-0"
                />
                {(cookingTimeMinutes || cookingTimeInputFocused) && (
                  <span className="absolute -top-2 left-2 px-1 text-xs font-medium bg-white text-gray-700">
                    {window.innerWidth < 768
                      ? 'Time (min)'
                      : 'Cooking Time (min)'}
                  </span>
                )}
              </div>
            </div>
          </Card.Body>
          <Card.Footer className="flex justify-end space-x-2">
            <Button variant="secondary" type="button" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={loading || checkingDuplicate}
            >
              {loading ? 'Saving' : recipe ? 'Update' : 'Save'}
            </Button>
          </Card.Footer>
        </form>
      </Card>
    </>
  )
}

export default RecipeForm
